// ============================================
// SessionSidebar.jsx - Session Manager Panel
// ============================================
// Left sidebar for managing chat sessions
// Features: New chat, search, session list, pinning, archiving

import React, { useState, useCallback } from 'react';
import {
  MessageSquare,
  Plus,
  Search,
  MoreVertical,
  Pin,
  Archive,
  Trash2,
  Edit3,
  Clock,
  Settings,
  PanelLeftClose,
  X,
  Check,
} from 'lucide-react';
import GhostLogo from './GhostLogo';

// ============================================
// TIME AGO HELPER
// ============================================
function getTimeAgo(date) {
  if (!date) return '';
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return new Date(date).toLocaleDateString();
}

// ============================================
// SESSION ITEM COMPONENT
// ============================================
function SessionItem({
  session,
  isActive,
  onClick,
  onPin,
  onArchive,
  onDelete,
  onRename,
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(session.title);

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleRename = () => {
    if (editTitle.trim() && editTitle !== session.title) {
      onRename(session.id, editTitle.trim());
    }
    setIsEditing(false);
    setShowMenu(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setEditTitle(session.title);
      setIsEditing(false);
    }
  };

  return (
    <div
      className={`session-item ${isActive ? 'active' : ''} ${session.isPinned ? 'pinned' : ''}`}
      onClick={onClick}
    >
      <div className="session-item-icon">
        <MessageSquare size={16} />
      </div>

      <div className="session-item-content">
        {isEditing ? (
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleRename}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            style={{
              width: '100%',
              padding: '2px 4px',
              fontSize: '13px',
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid var(--gi-green-600)',
              borderRadius: '4px',
              color: 'white',
            }}
          />
        ) : (
          <>
            <span className="session-item-title">{session.title}</span>
            <div className="session-item-meta">
              <span className="session-item-preview">{session.preview}</span>
              <span className="session-item-time">{getTimeAgo(session.updatedAt)}</span>
            </div>
          </>
        )}
      </div>

      <button className="session-item-menu" onClick={handleMenuClick}>
        <MoreVertical size={14} />
      </button>

      {showMenu && (
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 999,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(false);
            }}
          />
          <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
            <button
              className="dropdown-item"
              onClick={() => {
                setIsEditing(true);
                setShowMenu(false);
              }}
            >
              <Edit3 size={14} /> Rename
            </button>
            <button
              className="dropdown-item"
              onClick={() => {
                onPin(session.id);
                setShowMenu(false);
              }}
            >
              <Pin size={14} /> {session.isPinned ? 'Unpin' : 'Pin'}
            </button>
            <button
              className="dropdown-item"
              onClick={() => {
                onArchive(session.id);
                setShowMenu(false);
              }}
            >
              <Archive size={14} /> Archive
            </button>
            <div className="dropdown-divider" />
            <button
              className="dropdown-item danger"
              onClick={() => {
                onDelete(session.id);
                setShowMenu(false);
              }}
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================
// MAIN SIDEBAR COMPONENT
// ============================================
export default function SessionSidebar({
  sessions = [],
  currentSessionId,
  onSessionSelect,
  onNewSession,
  onDeleteSession,
  onPinSession,
  onArchiveSession,
  onRenameSession,
  onClose,
  onOpenSettings,
  // Auth props
  user = null,
  usage = null,
  onLogin,
  onSignup,
  onLogout,
}) {
  const [search, setSearch] = useState('');

  // Filter and sort sessions
  const filteredSessions = sessions
    .filter(s => !s.isArchived)
    .filter(s => s.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      // Pinned first
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      // Then by date
      return new Date(b.updatedAt) - new Date(a.updatedAt);
    });

  const pinnedSessions = filteredSessions.filter(s => s.isPinned);
  const recentSessions = filteredSessions.filter(s => !s.isPinned);

  return (
    <div className="sidebar-content" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-header-top">
          <div className="sidebar-brand">
            <GhostLogo size={26} animated />
            <span className="brand-name">GhostInk AI</span>
          </div>
          <button className="sidebar-close-btn" onClick={onClose}>
            <PanelLeftClose size={18} />
          </button>
        </div>

        {/* New Chat Button */}
        <button className="new-chat-btn" onClick={onNewSession}>
          <Plus size={18} />
          <span>New Chat</span>
        </button>
      </div>

      {/* Search */}
      <div className="sidebar-search">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search conversations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{ color: 'var(--gi-gray-500)' }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Sessions List */}
      <div className="sessions-list" style={{ flex: 1, overflowY: 'auto' }}>
        {/* Pinned Section */}
        {pinnedSessions.length > 0 && (
          <>
            <div className="sessions-section">
              <span className="sessions-section-title"><Pin size={12} /> Pinned</span>
            </div>
            {pinnedSessions.map(session => (
              <SessionItem
                key={session.id}
                session={session}
                isActive={currentSessionId === session.id}
                onClick={() => onSessionSelect(session)}
                onPin={onPinSession}
                onArchive={onArchiveSession}
                onDelete={onDeleteSession}
                onRename={onRenameSession}
              />
            ))}
          </>
        )}

        {/* Recent Section */}
        {recentSessions.length > 0 && (
          <>
            <div className="sessions-section">
              <span className="sessions-section-title">Recent</span>
            </div>
            {recentSessions.map(session => (
              <SessionItem
                key={session.id}
                session={session}
                isActive={currentSessionId === session.id}
                onClick={() => onSessionSelect(session)}
                onPin={onPinSession}
                onArchive={onArchiveSession}
                onDelete={onDeleteSession}
                onRename={onRenameSession}
              />
            ))}
          </>
        )}

        {/* Empty State */}
        {filteredSessions.length === 0 && (
          <div className="empty-state" style={{ padding: '32px 16px' }}>
            <MessageSquare size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
            <p style={{ fontSize: 13, color: 'var(--gi-gray-500)' }}>
              {search ? 'No matching conversations' : 'No conversations yet'}
            </p>
          </div>
        )}
      </div>

      {/* Footer - Account Card */}
      <div style={{
        padding: '12px',
        marginTop: 'auto',
      }}>
        <div style={{
          background: '#0a0a0a',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          padding: '16px',
        }}>
          {user ? (
            <>
              {/* User Info Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #059669, #047857)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  flexShrink: 0,
                }}>
                  {user.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#e5e5e5',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginBottom: '4px',
                  }}>
                    {user.email}
                  </div>
                  <div style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    background: user.tier === 'pro' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(82, 82, 82, 0.3)',
                    color: user.tier === 'pro' ? '#34d399' : '#a3a3a3',
                  }}>
                    {user.tier || 'Free'}
                  </div>
                </div>
              </div>

              {/* Usage Bar */}
              {usage && (
                <div style={{
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                  marginBottom: '16px',
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}>
                    <span style={{
                      fontSize: '11px',
                      color: '#737373',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>Messages</span>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#d4d4d4',
                    }}>{usage.remaining} / {usage.dailyLimit}</span>
                  </div>
                  <div style={{
                    height: '4px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '2px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${Math.min(100, ((usage.dailyLimit - usage.remaining) / usage.dailyLimit) * 100)}%`,
                      background: 'linear-gradient(90deg, #10b981, #34d399)',
                      borderRadius: '2px',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '8px',
                paddingTop: usage ? '0' : '16px',
                borderTop: usage ? 'none' : '1px solid rgba(255, 255, 255, 0.06)',
              }}>
                <button
                  onClick={onOpenSettings}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px 12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#a3a3a3',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <Settings size={14} />
                  Settings
                </button>
                <button
                  onClick={onLogout}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#737373',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Sign out
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Guest State */}
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#e5e5e5',
                  marginBottom: '4px',
                }}>Sign in to save your chats</div>
                <div style={{
                  fontSize: '12px',
                  color: '#737373',
                }}>Get more messages & sync devices</div>
              </div>

              {usage && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}>
                    <span style={{
                      fontSize: '11px',
                      color: '#737373',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>Guest messages</span>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#d4d4d4',
                    }}>{usage.remaining} / {usage.dailyLimit}</span>
                  </div>
                  <div style={{
                    height: '4px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '2px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${Math.min(100, ((usage.dailyLimit - usage.remaining) / usage.dailyLimit) * 100)}%`,
                      background: 'linear-gradient(90deg, #10b981, #34d399)',
                      borderRadius: '2px',
                    }} />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={onLogin}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#d4d4d4',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Sign in
                </button>
                <button
                  onClick={onSignup}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'white',
                    background: 'linear-gradient(135deg, #059669, #047857)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Sign up
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
